import React, { useState } from 'react';
import TranslationSettings from '../components/TranslationSettings';
import { useAuth } from '../context/AuthContext';
import { changePassword, updateProfile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Key, User, Save } from 'lucide-react';

export default function AccountSettings() {
  const { user, checkAuth } = useAuth();

  // Name edit
  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleSaveName = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    if (trimmed === user?.name) { toast.info('No changes to save'); return; }
    setSavingName(true);
    try {
      await updateProfile({ name: trimmed });
      toast.success('Name updated');
      checkAuth(); // refresh user context
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally { setSavingName(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPassword(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  return (
    <div className="space-y-6 max-w-lg mx-auto py-4" data-testid="account-settings-page">
      <div className="text-center pb-2">
        <h2 className="text-xl font-bold">My Account</h2>
        <p className="text-sm text-muted-foreground mt-1">Update your name or change your password</p>
      </div>

      {/* Language & Translations */}
      <TranslationSettings isAdmin={['super_admin', 'admin'].includes(user?.role)} />

      {/* Name Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="w-4 h-4" /> Display Name
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              className="h-12 text-base"
              data-testid="input-account-name"
            />
          </div>
          <Button onClick={handleSaveName} disabled={savingName} className="h-11 w-full rounded-xl" data-testid="btn-save-name">
            {savingName ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Name
          </Button>
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Key className="w-4 h-4" /> Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
                className="h-12 text-base"
                data-testid="input-account-current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="h-12 text-base"
                data-testid="input-account-new-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className="h-12 text-base"
                data-testid="input-account-confirm-password"
              />
            </div>
            <Button type="submit" disabled={changingPassword} className="h-11 w-full rounded-xl" data-testid="btn-account-change-password">
              {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
